import dayjs from "dayjs"
import React from "react"
import { useQuery } from "urql"

import { useManualBooking } from "../../../../contexts/ManualBookingContext"
import { usePractice } from "../../../../contexts/PracticeContext"
import { useToast } from "../../../../contexts/ToastContext"
import { formatClientPhonePlusOne, isClientValid } from "../../../../utils/utils"
import { Button } from "../../../shared/Buttons"

import { needsProMissingCardAcknowledge } from "./needsProMissingCardAcknowledge"

const DEFAULT_PAYMENT_METHOD_QUERY = `
  query($clientId: ID, $userId: ID, $practiceId: ID!) {
    defaultPaymentMethodPractice(clientId: $clientId, userId: $userId, practiceId: $practiceId) {
      brand
      last4
    }
  }
`

const AppointmentActions = ({
  fetching,
  hasIntakeFormChanges = false,
  onDecline,
  removeAppointmentFromCalendar,
  onAccept,
  onAppointmentChanged,
  onCancel,
  createManualAppointment,
  editManualAppointment,
  onRecurringAppointmentsChanged,
  resetBookingState,
  setShowPackageConfirmModal,
  setShowMissingCardConfirmModal,
  setMissingCardConfirmMode,
  setShowMissingPhoneConfirmModal,
  setMissingPhoneConfirmMode,
  setShowPastAppointmentModal,
  setPendingAction
}) => {
  const { showToast } = useToast()
  const { practice } = usePractice()

  const {
    appointment,
    services,
    client,
    locationId,
    startsAt,
    endsAt,
    recurringStartTimes,
    recurrencePattern,
    createRecurringAppointments,
    recurringAppointmentError,
    setAppointmentErrors,
    initialAppointmentValues
  } = useManualBooking()

  const { practicePhone, practiceEmail } = usePractice()

  const pending = appointment?.state === "pending"
  const packageData =
    client?.openPackages?.find((p) => p.serviceId === services?.[0]?.service?.id) || appointment?.package
  const clientPhone = formatClientPhonePlusOne(client?.phone)

  const amountCents = services?.reduce((sum, service) => sum + (service.amountCents || 0), 0) ?? 0
  const isCreatingNewPackage = services?.[0]?.service?.package && !packageData
  const isPaidNonPackage = amountCents > 0 && !services?.[0]?.service?.package

  const clientId = client?.__typename === "Client" ? client?.id : null
  const userId = client?.__typename === "User" ? client?.id : null
  // Include new clients without an id — otherwise Save never sends acknowledgeMissingCard
  // and the create mutation keeps failing with "Confirm to book anyway...".
  const needsCardCheck = needsProMissingCardAcknowledge({
    appointment,
    practice,
    isPaidNonPackage,
    client
  })
  const confirmMissingCardEnabled = !!practice?.confirmMissingCardEnabled
  // Only fetch card when the confirm modal can show and we have a persisted client.
  const shouldQueryCard = needsCardCheck && confirmMissingCardEnabled && !!client?.id
  // phoneOnFile mirrors Client#phone_on_file? (practice Client row only).
  const missingPhoneOnFile = !(client?.phoneOnFile ?? !!client?.phone)
  const needsPhoneCheck = !appointment && !!practice?.requireClientPhoneToBookEnabled && missingPhoneOnFile

  const [{ data: cardData, fetching: cardFetching }] = useQuery({
    query: DEFAULT_PAYMENT_METHOD_QUERY,
    variables: { clientId, userId, practiceId: practice?.id },
    pause: !shouldQueryCard
  })

  const cardOnFile = cardData?.defaultPaymentMethodPractice

  const hasChangesInAppointment = () => {
    if (!appointment || !initialAppointmentValues) return true

    return (
      initialAppointmentValues.locationId !== locationId ||
      initialAppointmentValues.startsAt !== startsAt ||
      initialAppointmentValues.endsAt !== endsAt ||
      JSON.stringify(initialAppointmentValues.services) !== JSON.stringify(services) ||
      JSON.stringify(initialAppointmentValues.client) !== JSON.stringify(client) ||
      hasIntakeFormChanges
    )
  }

  const isDisabled =
    (recurrencePattern && recurringStartTimes?.length === 0) ||
    !!recurringAppointmentError ||
    fetching ||
    (shouldQueryCard && cardFetching) ||
    !services?.[0]?.service ||
    !locationId ||
    !client ||
    !isClientValid(client, clientPhone, practicePhone, practiceEmail) ||
    (appointment && !hasChangesInAppointment())

  const isPastAppointment = () => dayjs(startsAt).isBefore(dayjs())

  const promptMissingCardConfirm = (mode) => {
    setMissingCardConfirmMode(mode)
    setShowMissingCardConfirmModal(true)
  }

  const promptMissingPhoneConfirm = (mode) => {
    setMissingPhoneConfirmMode(mode)
    setShowMissingPhoneConfirmModal(true)
  }

  const handleSave = () => {
    const validServices = services?.filter((service) => service.service)

    if (validServices?.length > 0 && validServices?.every((service) => service.service?.addOn)) {
      setAppointmentErrors("You can't create an appointment with only add-on services")
      return
    }

    if (isPastAppointment() && !appointment) {
      setShowPastAppointmentModal(true)
      setPendingAction(() => executeSave)
      return
    }

    executeSave()
  }

  const bookWithoutCardModal = (mode, { acknowledgeMissingPhone = false } = {}) => {
    if (confirmMissingCardEnabled) {
      // New clients have no id yet → no card to query; treat as missing and confirm.
      if (client?.id && cardFetching) return true
      if (!client?.id || !cardOnFile) {
        promptMissingCardConfirm(mode)
        return true
      }
      return false
    }
    // ENV off (default): skip modal, acknowledge so backend allows pro booking.
    if (mode === "recurring") {
      createRecurringAppointments(
        (appointments) => {
          resetBookingState()
          onRecurringAppointmentsChanged(appointments)
          setShowPackageConfirmModal(false)
        },
        { acknowledgeMissingCard: true, acknowledgeMissingPhone }
      )
    } else {
      createManualAppointment({ acknowledgeMissingCard: true, acknowledgeMissingPhone })
      setShowPackageConfirmModal(false)
    }
    return true
  }

  const bookWithoutPhoneModal = (mode) => {
    if (!needsPhoneCheck) return false
    promptMissingPhoneConfirm(mode)
    return true
  }

  const executeSave = () => {
    if (appointment) {
      editManualAppointment()
    } else {
      if (recurringStartTimes && recurrencePattern) {
        if (services?.[0]?.service?.package && !packageData) {
          setShowPackageConfirmModal(true)
        } else if (bookWithoutPhoneModal("recurring")) {
          // handled: missing phone modal
        } else if (needsCardCheck && bookWithoutCardModal("recurring")) {
          // handled: modal shown, waiting for card fetch, or auto-acknowledged
        } else {
          createRecurringAppointments((appointments) => {
            resetBookingState()
            onRecurringAppointmentsChanged(appointments)
            setShowPackageConfirmModal(false)
          })
        }
      } else {
        if (isCreatingNewPackage) {
          setShowPackageConfirmModal(true)
        } else if (bookWithoutPhoneModal("appointment")) {
          // handled: missing phone modal
        } else if (needsCardCheck && bookWithoutCardModal("appointment")) {
          // handled: modal shown, waiting for card fetch, or auto-acknowledged
        } else {
          createManualAppointment()
          setShowPackageConfirmModal(false)
        }
      }
    }
  }

  return (
    <div className="mt-8 flex w-full justify-between border-t border-gray-light py-4">
      {pending ? (
        <>
          <Button
            type="warning"
            className="sm:w-full"
            onClick={() => {
              onDecline().then((result) => {
                if (result.data?.declineAppointment?.result === "success") {
                  removeAppointmentFromCalendar([appointment.id])
                  showToast("Appointment declined")
                } else {
                  showToast({
                    type: "error",
                    content: "Unexpected error declining appointment. Please contact support if this persists."
                  })
                }
              })
            }}>
            Decline
          </Button>
          <Button
            className="sm:w-full"
            type="primary"
            onClick={() => {
              onAccept().then((result) => {
                if (result.data.acceptAppointment?.result === "success") {
                  onAppointmentChanged(result.data.acceptAppointment.appointment)
                  showToast("Appointment accepted")
                } else {
                  showToast({
                    type: "error",
                    content: "Unexpected error accepting appointment. Please contact support if this persists."
                  })
                }
              })
            }}>
            Accept
          </Button>
        </>
      ) : (
        <>
          {appointment && (
            <Button type="warning" className="mr-4 sm:w-full" onClick={onCancel}>
              Cancel appt.
            </Button>
          )}
          <Button type="primary" className="sm:w-full" onClick={handleSave} disabled={isDisabled}>
            Save
          </Button>
        </>
      )}
    </div>
  )
}

export default AppointmentActions
